import dayjs from 'dayjs'

import {
  IConvertedTransactions,
  ISlotsHistory,
  ISlotsHistoryFilters,
  ISlotsHistoryPayload,
  ITransactionFilters,
  ITransactionFiltersPayload,
} from 'services/api/ProfileApi/types/types'

import { IUserInfoPayload, IWithdrawalPayload } from 'shared/models/Profile'
import { makeCommunicationActionCreator } from 'shared/utils/communication/actions/makeCommunicationActionCreator'

import { getPlayerPrivacySettings } from 'features/lastWins/actions'

import { addNotify } from 'components/Notify'

import { IDefaultChangePasswordState, IForceChangePasswordPayload } from './types'

export const getProfileInfo = makeCommunicationActionCreator({
  loading: '@profile/GET_PROFILE_INFO_LOADING',
  success: '@profile/GET_PROFILE_INFO_SUCCESS',
  error: '@profile/GET_PROFILE_INFO_ERROR',
  reset: '@profile/GET_PROFILE_INFO_RESET',
})(async ({
  deps: {
    extra: { api },
  },
}) => {
  const response = await api.profile.getProfileInfo()
  return response.data
})

export const getUserInfo = makeCommunicationActionCreator({
  loading: '@profile/GET_USER_INFO_LOADING',
  success: '@profile/GET_USER_INFO_SUCCESS',
  error: '@profile/GET_USER_INFO_ERROR',
  reset: '@profile/GET_USER_INFO_RESET',
})(async ({
  deps: {
    extra: { api },
  },
}) => {
  const response = await api.profile.getUserInfo()
  return response.data
})

export const updateUserInfo = makeCommunicationActionCreator({
  loading: '@profile/UPDATE_USER_INFO_LOADING',
  success: '@profile/UPDATE_USER_INFO_SUCCESS',
  error: '@profile/UPDATE_USER_INFO_ERROR',
  reset: '@profile/UPDATE_USER_INFO_RESET',
})<IUserInfoPayload, boolean>(async ({
  deps: {
    extra: { api },
  },
  payload,
}) => {
  const newProfileInfo = {
    first_name: payload.firstName,
    last_name: payload.lastName,
    date_of_birth: payload.dateOfBirth,
    mobile_number: payload.mobileNumber || '',
    postal_code: payload.postalCode || '',
    street_address: payload.streetAddress,
    country: payload.country,
    city: payload.city,
    gender: payload.gender,
  }

  const response = await api.profile.updateUserInfo(newProfileInfo)
  return response.data
})

export const changePassword = makeCommunicationActionCreator({
  loading: '@profile/CHANGE_PASSWORD_LOADING',
  success: '@profile/CHANGE_PASSWORD_SUCCESS',
  error: '@profile/CHANGE_PASSWORD_ERROR',
  reset: '@profile/CHANGE_PASSWORD_RESET',
})<IDefaultChangePasswordState, void>(async ({
  deps: {
    extra: { api },
    getState,
  },
  payload,
}) => {
  const { data, callback } = payload
  const { oldPassword, newPassword, repeatNewPassword } = data
  const { profile }: any = getState().locale.data

  if (newPassword === repeatNewPassword) {
    const response = await api.profile.changePassword({ old_password: oldPassword, new_password: newPassword })
    if (response.success) {
      callback()
    }
  } else {
    addNotify(profile.changedPasswordDoNotMatch, 'error')
  }
})

export const forceChangePassword = makeCommunicationActionCreator({
  loading: '@profile/CHANGE_PASSWORD_FORCE_LOADING',
  success: '@profile/CHANGE_PASSWORD_FORCE_SUCCESS',
  error: '@profile/CHANGE_PASSWORD_FORCE_ERROR',
  reset: '@profile/CHANGE_PASSWORD_FORCE_RESET',
})<IForceChangePasswordPayload, void, { callback?: () => void; errorCallback?: () => void }>(
  async ({
    deps: {
      extra: { api },
      getState,
    },
    payload,
    args,
  }) => {
    const { profile } = getState().locale.data

    try {
      await api.profile.forceChangePassword({ new_password: payload.newPassword })
      args?.callback?.()
      // setTimeout(() => addNotify(profile.changedPassword, 'success'), 300)
    } catch {
      args?.errorCallback?.()
    }
  },
)

export const getPayHistory = makeCommunicationActionCreator({
  loading: '@profile/GET_PAY_HISTORY_LOADING',
  success: '@profile/GET_PAY_HISTORY_SUCCESS',
  error: '@profile/GET_PAY_HISTORY_ERROR',
  reset: '@profile/GET_PAY_HISTORY_RESET',
})<ITransactionFiltersPayload, IConvertedTransactions>(async ({
  deps: {
    extra: { api },
    getState,
  },
  payload,
}) => {
  const { user } = getState().auth.data

  const newFilters: ITransactionFilters = {
    page: payload.page,
    page_size: payload.pageSize,
    created_from: payload.createdFrom ? dayjs(payload.createdFrom).startOf('day').format('YYYY-MM-DDTHH:mm:ssZ') : '',
    created_to: payload.createdTo
      ? dayjs(payload.createdTo).add(1, 'day').startOf('day').format('YYYY-MM-DDTHH:mm:ssZ')
      : '',
    status: payload.status,
    sender: payload.type === 'withdrawal' ? String(user?.id) : undefined,
    recipient: payload.type === 'deposit' ? String(user?.id) : undefined,
  }

  const response = await api.profile.loadTransfersList(newFilters)
  return response.data
})

export const withdrawCashier = makeCommunicationActionCreator({
  loading: '@profile/WITHDRAW_CASHIER_LOADING',
  success: '@profile/WITHDRAW_CASHIER_SUCCESS',
  error: '@profile/WITHDRAW_CASHIER_ERROR',
  reset: '@profile/WITHDRAW_CASHIER_RESET',
})<IWithdrawalPayload, any>(async ({
  deps: {
    extra: { api },
    getState,
  },
  payload,
}) => {
  const locale = getState().locale.data.payment

  const newPayload = {
    amount: payload.amount,
    currency: payload.currency,
    sender_id: payload.senderId,
    recipient_id: payload.recipientId,
  }

  const response = await api.profile.withdrawCashier(newPayload)
  if (response.success) {
    addNotify(locale.withdrawalSuccess, 'success')
  }
  return response.data
})

export const sendKYCPicture = makeCommunicationActionCreator({
  loading: '@profile/SEND_KYC_PICTURE_LOADING',
  success: '@profile/SEND_KYC_PICTURE_SUCCESS',
  error: '@profile/SEND_KYC_PICTURE_ERROR',
  reset: '@profile/SEND_KYC_PICTURE_RESET',
})<FormData, void, { callback: () => void }>(async ({
  deps: {
    extra: { api },
    getState,
  },
  payload,
  args,
}) => {
  const locale = getState().locale.data.profile
  const response = await api.profile.sendKYCpicture(payload)
  if (response.success) {
    addNotify(locale.kycSendedSuccess, 'success')
  }
  if (args?.callback) {
    args.callback()
  }
})

export const getSlotsHistory = makeCommunicationActionCreator({
  loading: '@profile/GET_SLOTS_HISTORY_LOADING',
  success: '@profile/GET_SLOTS_HISTORY_SUCCESS',
  error: '@profile/GET_SLOTS_HISTORY_ERROR',
  reset: '@profile/GET_SLOTS_HISTORY_RESET',
})<ISlotsHistoryPayload, ISlotsHistory>(async ({
  deps: {
    extra: { api },
  },
  payload,
}) => {
  const filters: ISlotsHistoryFilters = {
    date_from: dayjs(payload.beginDate).startOf('day').format('YYYY-MM-DDTHH:mm:ssZ'),
    date_to: dayjs(payload.endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ'),
    page: payload.page,
    page_size: payload.pageSize,
    producer: payload.provider,
  }

  const response = await api.profile.getSlotsHistory(filters)

  return response.data
})

export const changePrivacy = makeCommunicationActionCreator({
  loading: '@profile/SEND_CHANGE_PRIVACY_LOADING',
  success: '@profile/SEND_CHANGE_PRIVACY_SUCCESS',
  error: '@profile/SEND_CHANGE_PRIVACY_ERROR',
  reset: '@profile/SEND_CHANGE_PRIVACY_RESET',
})<void, void>(async ({
  deps: {
    extra: { api },
    dispatch,
    getState,
  },
}) => {
  const locale = getState().locale.data.profile
  const response = await api.profile.changePrivacy()
  if (response.success) {
    addNotify(locale.changePrivacy, 'success')
    dispatch(getPlayerPrivacySettings())
  }
})
